/**
 * Field Query System — ask the field, and it answers spatially.
 *
 * Press / to open query input. Type a question.
 * The field responds:
 *   1. Relevant nodes brighten, irrelevant dim
 *   2. Camera drifts toward the answer region
 *   3. A synthesis line appears as floating spatial text
 *
 * The AI lives INSIDE the field, not beside it.
 * No chat panel. The field IS the answer surface.
 *
 * Phase 1 (live preview): keyword matching as you type — instant.
 * Phase 2 (on Enter): GPT-4 semantic analysis — real reasoning.
 */

import * as THREE from 'three';
import type { SyntheticField, SyntheticNode } from '../data/synthetic';
import type { FieldCamera } from '../scene/camera';
import type { NodeRenderer } from '../scene/nodes';
import type { ZoneNavigation } from '../scene/zone';
import { useAuraStore } from '../state/store';

/** Per-node relevance score from a query */
interface QueryResult {
  nodeScores: Float32Array;  // 0–1 relevance per node
  topCategory: string;
  topCluster: { name: string; index: number; score: number };
  centroid: THREE.Vector3;   // weighted center of relevant nodes
  synthesis: string;         // generated summary text
}

/** What the LLM returns from the backend */
interface LLMResponse {
  clusterScores: Record<string, number>;
  topCategory: string;
  synthesis: string;
  momentum: 'rising' | 'stable' | 'fading';
}

/** Pre-built keyword index for fast matching */
interface KeywordEntry {
  nodeIndex: number;
  terms: string[];           // lowercased words from name + category + trend
  node: SyntheticNode;
}

export class QuerySystem {
  private active = false;
  private loading = false;
  private inputEl: HTMLElement | null;
  private inputField: HTMLInputElement | null;
  private synthesisEl: HTMLElement | null;
  private hintEl: HTMLElement | null;
  private currentResult: QueryResult | null = null;
  private highlightProgress = 0;     // 0 = no highlight, 1 = fully highlighted
  private targetHighlight = 0;       // what we're easing toward
  private keywordIndex: KeywordEntry[] = [];

  // Map from cluster name → node indices (cluster + its satellites)
  private clusterNodeMap: Map<string, number[]> = new Map();

  constructor(
    private field: SyntheticField,
    private fieldCamera: FieldCamera,
    private nodeRenderer: NodeRenderer,
    private zoneNavigation: ZoneNavigation,
  ) {
    // Build keyword index
    this.buildIndex();
    this.buildClusterMap();

    // Cache DOM
    this.inputEl = document.getElementById('query-input');
    this.inputField = document.getElementById('query-field') as HTMLInputElement;
    this.synthesisEl = document.getElementById('query-synthesis');
    this.hintEl = document.getElementById('query-hint');

    this.bindEvents();
  }

  private buildIndex() {
    this.keywordIndex = this.field.nodes.map((node, i) => {
      const terms = [
        ...node.name.toLowerCase().split(/[\s\-\/&,]+/),
        ...node.category.toLowerCase().split(/[\s\-\/&,]+/),
        node.trend,
      ].filter(Boolean);

      // Generic semantic expansions — domain-agnostic synonyms
      const expansions: Record<string, string[]> = {
        'growth': ['growing', 'rising', 'surge', 'boom', 'expansion', 'increase'],
        'decline': ['fading', 'falling', 'shrinking', 'drop', 'decrease'],
        'trend': ['trending', 'movement', 'shift', 'emerging', 'momentum'],
        'innovation': ['new', 'novel', 'inventive', 'disruptive', 'tech', 'breakthrough'],
        'premium': ['luxury', 'craft', 'artisanal', 'high-end', 'quality'],
        'sustainable': ['sustainability', 'eco', 'green', 'organic', 'natural', 'ethical'],
        'experience': ['experiential', 'immersive', 'event', 'cultural', 'engagement'],
        'digital': ['ecommerce', 'online', 'delivery', 'subscription', 'platform'],
        'market': ['industry', 'sector', 'category', 'segment', 'space'],
        'consumer': ['customer', 'buyer', 'user', 'audience', 'demographic'],
        'data': ['signal', 'insight', 'intelligence', 'analytics', 'metric'],
        'ai': ['artificial intelligence', 'machine learning', 'model', 'llm', 'automation'],
      };

      for (const term of [...terms]) {
        const expanded = expansions[term];
        if (expanded) terms.push(...expanded);
      }

      return { nodeIndex: i, terms, node };
    });
  }

  /**
   * Build a map from cluster name → all node indices belonging to that cluster.
   * This lets us propagate LLM cluster-level scores to individual particles.
   */
  private buildClusterMap() {
    const clusterIndices: Map<string, number> = new Map();

    // First pass: find all cluster nodes
    for (let i = 0; i < this.field.nodes.length; i++) {
      const node = this.field.nodes[i];
      if (node.id.startsWith('cluster-')) {
        clusterIndices.set(node.name, i);
        this.clusterNodeMap.set(node.name, [i]);
      }
    }

    // Second pass: assign satellites to their parent clusters via edges
    for (const edge of this.field.edges) {
      const srcNode = this.field.nodes[edge.source];
      const tgtNode = this.field.nodes[edge.target];
      if (srcNode && tgtNode) {
        // If source is satellite and target is cluster, or vice versa
        if (this.clusterNodeMap.has(tgtNode.name) && !this.clusterNodeMap.has(srcNode.name)) {
          const indices = this.clusterNodeMap.get(tgtNode.name)!;
          if (indices.indexOf(edge.source) === -1) indices.push(edge.source);
        } else if (this.clusterNodeMap.has(srcNode.name) && !this.clusterNodeMap.has(tgtNode.name)) {
          const indices = this.clusterNodeMap.get(srcNode.name)!;
          if (indices.indexOf(edge.target) === -1) indices.push(edge.target);
        }
      }
    }
  }

  private onGlobalKeyDown = (e: KeyboardEvent) => {
    const store = useAuraStore.getState();
    if (!store.entryCompleted) return;

    if (e.key === '/' && !this.active && document.activeElement?.tagName !== 'INPUT') {
      e.preventDefault();
      this.open();
    }

    if (e.key === 'Escape' && this.active) {
      e.preventDefault();
      e.stopPropagation();
      this.close();
    }
  };

  private bindEvents() {
    document.addEventListener('keydown', this.onGlobalKeyDown);

    // Input handling
    this.inputField?.addEventListener('input', () => {
      this.onQueryChange();
    });

    this.inputField?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.executeQuery();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      }
    });
  }

  open() {
    this.active = true;
    this.inputEl?.classList.add('visible');
    this.hintEl?.classList.remove('visible');

    // Clear all depth navigation panels — query takes over the field
    this.zoneNavigation.forceOverview();

    // Slide mode switcher out of the way
    const modeSwitcher = document.getElementById('mode-switcher');
    if (modeSwitcher) modeSwitcher.style.opacity = '0';

    // Small delay before focus to let animation start
    requestAnimationFrame(() => {
      this.inputField?.focus();
    });
  }

  close() {
    this.active = false;
    this.loading = false;
    this.inputEl?.classList.remove('visible');
    this.inputEl?.classList.remove('loading');
    this.synthesisEl?.classList.remove('visible');

    if (this.inputField) {
      this.inputField.value = '';
      this.inputField.blur();
    }

    // Fade out highlighting
    this.targetHighlight = 0;
    this.currentResult = null;

    // Restore mode switcher
    const modeSwitcher = document.getElementById('mode-switcher');
    if (modeSwitcher) modeSwitcher.style.opacity = '';

    // Show hint again after a delay
    setTimeout(() => {
      if (!this.active) {
        this.hintEl?.classList.add('visible');
      }
    }, 2000);
  }

  private onQueryChange() {
    const query = this.inputField?.value.trim() ?? '';
    if (query.length < 2) {
      this.targetHighlight = 0;
      this.currentResult = null;
      this.synthesisEl?.classList.remove('visible');
      return;
    }

    // Phase 1: Live keyword preview — instant spatial feedback while typing
    this.currentResult = this.matchKeywords(query);
    this.targetHighlight = 1;
  }

  // ─── Query Execution (Phase 2: LLM) ────────────────────────

  private async executeQuery() {
    const query = this.inputField?.value.trim() ?? '';
    if (query.length < 2 || this.loading) return;

    // Show loading state — immediately signal that the field is thinking
    this.loading = true;
    this.inputEl?.classList.add('loading');
    this.showLoadingState(query);

    // Immediate keyword-based spatial feedback (Phase 1 fallback)
    const keywordResult = this.matchKeywords(query);
    this.currentResult = keywordResult;
    this.targetHighlight = 1;

    // Enter the keyword-matched zone right away (instant feedback)
    if (keywordResult.topCategory) {
      this.zoneNavigation.enterZoneContext(keywordResult.topCategory);
    }
    if (keywordResult.centroid.length() > 0) {
      this.fieldCamera.diveTo(keywordResult.centroid, 250);
    }

    // Now call the LLM for the real answer
    try {
      const llmResponse = await this.callLLM(query);
      if (!this.active) return; // User closed while waiting

      // Map LLM cluster scores → per-particle node scores
      const llmResult = this.mapLLMResponse(llmResponse);

      // Smooth transition: replace keyword result with LLM result
      this.currentResult = llmResult;

      // Update zone context if LLM found a different top category
      if (llmResult.topCategory !== keywordResult.topCategory) {
        this.zoneNavigation.enterZoneContext(llmResult.topCategory);
      }

      // Camera adjusts to LLM centroid if significantly different
      if (llmResult.centroid.length() > 0) {
        const drift = llmResult.centroid.distanceTo(keywordResult.centroid);
        if (drift > 50) {
          this.fieldCamera.diveTo(llmResult.centroid, 250);
        }
      }

      // Show LLM synthesis (replaces template text)
      this.showSynthesis(llmResult);

    } catch (err) {
      // LLM failed — keyword result already active, just log it
      console.warn('[AURA Query] LLM call failed, using keyword fallback:', err);
    } finally {
      this.loading = false;
      this.inputEl?.classList.remove('loading');
      this.synthesisEl?.classList.remove('loading');
    }
  }

  /** Call the backend /api/query endpoint */
  private async callLLM(query: string): Promise<LLMResponse> {
    const response = await fetch('/api/query', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(err.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Map LLM cluster-level scores to per-particle relevance.
   * The LLM scores ~65 clusters. We propagate those scores to 250K particles.
   */
  private mapLLMResponse(llm: LLMResponse): QueryResult {
    const nodeScores = new Float32Array(this.field.nodes.length);

    // Apply LLM scores via cluster map
    for (const [clusterName, score] of Object.entries(llm.clusterScores)) {
      const indices = this.clusterNodeMap.get(clusterName);
      if (!indices) continue;

      for (let i = 0; i < indices.length; i++) {
        const idx = indices[i];
        // Cluster node gets full score, satellites get slightly reduced
        const isCluster = i === 0;
        nodeScores[idx] = isCluster ? score : score * 0.85;
      }
    }

    // Find top cluster
    let topCluster = { name: '', index: -1, score: 0 };
    for (const [clusterName, score] of Object.entries(llm.clusterScores)) {
      if (score > topCluster.score) {
        const indices = this.clusterNodeMap.get(clusterName);
        if (indices && indices.length > 0) {
          topCluster = { name: clusterName, index: indices[0], score };
        }
      }
    }

    // Compute weighted centroid
    const centroid = new THREE.Vector3();
    let totalWeight = 0;
    for (let i = 0; i < this.field.nodes.length; i++) {
      const s = nodeScores[i];
      if (s > 0.2) {
        const pos = this.field.nodes[i].position;
        centroid.x += pos[0] * s;
        centroid.y += pos[1] * s;
        centroid.z += pos[2] * s;
        totalWeight += s;
      }
    }
    if (totalWeight > 0) {
      centroid.divideScalar(totalWeight);
    }

    return {
      nodeScores,
      topCategory: llm.topCategory,
      topCluster,
      centroid,
      synthesis: llm.synthesis,
    };
  }

  // ─── Keyword Matching Engine (Phase 1 — instant preview) ───

  private matchKeywords(query: string): QueryResult {
    const queryTerms = query.toLowerCase().split(/[\s\-\/&,?]+/).filter(t => t.length > 1);
    const nodeScores = new Float32Array(this.field.nodes.length);

    // Score each node
    for (const entry of this.keywordIndex) {
      let score = 0;

      for (const qTerm of queryTerms) {
        for (const nTerm of entry.terms) {
          if (nTerm === qTerm) {
            score += 1.0;   // Exact match
          } else if (nTerm.includes(qTerm) || qTerm.includes(nTerm)) {
            score += 0.6;   // Partial match
          } else if (this.fuzzyMatch(qTerm, nTerm)) {
            score += 0.3;   // Fuzzy match
          }
        }
      }

      // Boost cluster nodes (they're the meaningful data, not satellites)
      if (entry.node.id.startsWith('cluster-')) {
        score *= 1.5;
      }

      // Boost by signal strength (stronger signals more relevant)
      score *= (0.5 + entry.node.intensity);

      nodeScores[entry.nodeIndex] = score;
    }

    // Normalize to 0–1
    let maxScore = 0;
    for (let i = 0; i < nodeScores.length; i++) {
      if (nodeScores[i] > maxScore) maxScore = nodeScores[i];
    }
    if (maxScore > 0) {
      for (let i = 0; i < nodeScores.length; i++) {
        nodeScores[i] /= maxScore;
      }
    }

    // Find top category and cluster
    const categoryScores = new Map<string, number>();
    let topCluster = { name: '', index: -1, score: 0 };

    for (let i = 0; i < this.field.nodes.length; i++) {
      const node = this.field.nodes[i];
      const s = nodeScores[i];
      categoryScores.set(node.category, (categoryScores.get(node.category) ?? 0) + s);

      if (s > topCluster.score && node.id.startsWith('cluster-')) {
        topCluster = { name: node.name, index: i, score: s };
      }
    }

    let topCategory = '';
    let topCatScore = 0;
    for (const [cat, score] of categoryScores) {
      if (score > topCatScore) {
        topCatScore = score;
        topCategory = cat;
      }
    }

    // Compute weighted centroid of top-scoring nodes
    const centroid = new THREE.Vector3();
    let totalWeight = 0;
    for (let i = 0; i < this.field.nodes.length; i++) {
      const s = nodeScores[i];
      if (s > 0.3) {
        const pos = this.field.nodes[i].position;
        centroid.x += pos[0] * s;
        centroid.y += pos[1] * s;
        centroid.z += pos[2] * s;
        totalWeight += s;
      }
    }
    if (totalWeight > 0) {
      centroid.divideScalar(totalWeight);
    }

    // Generate template synthesis (Phase 1 placeholder)
    const synthesis = this.synthesizeTemplate(query, topCategory, topCluster, nodeScores);

    return { nodeScores, topCategory, topCluster, centroid, synthesis };
  }

  private fuzzyMatch(a: string, b: string): boolean {
    if (a.length < 3 || b.length < 3) return false;
    return a.slice(0, 3) === b.slice(0, 3);
  }

  // ─── Template Synthesis (Phase 1 fallback) ─────────────────

  private synthesizeTemplate(
    query: string,
    topCategory: string,
    topCluster: { name: string; score: number },
    scores: Float32Array,
  ): string {
    let relevantCount = 0;
    let risingCount = 0;
    let fadingCount = 0;
    const relevantCategories = new Set<string>();

    for (let i = 0; i < this.field.nodes.length; i++) {
      if (scores[i] > 0.3) {
        relevantCount++;
        relevantCategories.add(this.field.nodes[i].category);
        if (this.field.nodes[i].trend === 'rising') risingCount++;
        if (this.field.nodes[i].trend === 'fading') fadingCount++;
      }
    }

    if (relevantCount === 0) {
      return `No strong signals found for "${query}" in the current field.`;
    }

    const parts: string[] = [];
    if (topCluster.name) {
      parts.push(`Strongest signal: ${topCluster.name}`);
    }
    if (relevantCategories.size === 1) {
      parts.push(`concentrated in ${topCategory}`);
    } else {
      parts.push(`spans ${relevantCategories.size} categories, centered on ${topCategory}`);
    }
    if (risingCount > fadingCount * 2) {
      parts.push(`momentum is strongly upward`);
    } else if (risingCount > fadingCount) {
      parts.push(`trending positive`);
    } else if (fadingCount > risingCount) {
      parts.push(`showing signs of deceleration`);
    }
    parts.push(`${relevantCount} related signals identified`);

    return parts.join(' · ');
  }

  // ─── Synthesis Display ────────────────────────────────────────

  private showLoadingState(query: string) {
    if (!this.synthesisEl) return;

    const synthText = this.synthesisEl.querySelector('#query-synthesis-text');
    const synthCategory = this.synthesisEl.querySelector('#query-synthesis-category');

    if (synthCategory) synthCategory.textContent = 'READING THE FIELD';
    if (synthText) synthText.textContent = `Analyzing signal patterns for "${query}"`;

    this.synthesisEl.classList.add('visible', 'loading');
  }

  private showSynthesis(result: QueryResult) {
    if (!this.synthesisEl) return;

    const synthText = this.synthesisEl.querySelector('#query-synthesis-text');
    const synthCategory = this.synthesisEl.querySelector('#query-synthesis-category');

    if (synthText) synthText.textContent = result.synthesis;
    if (synthCategory) synthCategory.textContent = result.topCategory;

    this.synthesisEl.classList.add('visible');
  }

  // ─── Per-frame update ─────────────────────────────────────────

  update() {
    // Ease highlight progress
    const speed = 0.04;
    if (this.highlightProgress < this.targetHighlight) {
      this.highlightProgress = Math.min(this.targetHighlight, this.highlightProgress + speed);
    } else if (this.highlightProgress > this.targetHighlight) {
      this.highlightProgress = Math.max(this.targetHighlight, this.highlightProgress - speed);
    }

    // Update shader uniform
    this.nodeRenderer.material.uniforms.u_queryHighlight.value = this.highlightProgress;

    // Update per-particle relevance attribute
    if (this.currentResult && this.highlightProgress > 0.01) {
      this.nodeRenderer.setQueryRelevance(this.currentResult.nodeScores);
    } else if (this.highlightProgress < 0.01) {
      this.nodeRenderer.clearQueryRelevance();
    }
  }

  isActive(): boolean {
    return this.active;
  }

  dispose() {
    document.removeEventListener('keydown', this.onGlobalKeyDown);
  }
}
